// "What does this business do?" (PRD 45 §8.1, asked at last by PRD 51 §4).
//
// ONE COMPONENT, TWO ENTRY POINTS: the second step of BusinessOnboarding, and
// the setup guide's first row for anyone who skipped it. That is deliberate —
// if the wizard asked one way and the guide another, there would be two places
// to keep in step and two answers to reconcile. There is one question here and
// one place it is answered.
//
// It writes businesses.default_experience: the BUSINESS's answer, inherited by
// every member who has not chosen a view of their own. It is NOT the personal
// preference the Customise panel edits, and it is NOT entity_type — that is
// the legal entity (LLC, S-corp), which PRD 45 §8.1 warned against overloading.
// A property owner can be any of them.
//
// SKIPPING IS A FIRST-CLASS OUTCOME. The column stays null, the resolver falls
// through to `general`, and the app behaves exactly as it does today. Nothing
// here gates anything, and the answer can be changed afterwards from this same
// component.

import React, { useCallback, useEffect, useState } from 'react';
import { XStack, YStack } from 'tamagui';
import { GBAlert, GBButton, GBCard, GBSkeleton, GBText } from '@greenbackapp/ui';
import { Check } from 'lucide-react';
import { businessExperienceService } from '../../../services/businessExperienceService';
import { businessService } from '../../../services/businessService';
import { BusinessApiError } from '../../../services/businessApiCommon';
import { BusinessExperienceOption } from '../../../types/businessApi';

interface ExperienceQuestionProps {
  businessId: string;
  /** Preselected when the business has already answered; null means never asked. */
  current?: string | null;
  /** Called after a successful save, and on skip with no key. */
  onDone: (chosen?: string) => void;
  /** The wizard offers "Skip for now"; the guide offers "Cancel". */
  skipLabel?: string;
}

export const ExperienceQuestion: React.FC<ExperienceQuestionProps> = ({
  businessId,
  current = null,
  onDone,
  skipLabel = 'Skip for now',
}) => {
  const [options, setOptions] = useState<BusinessExperienceOption[] | null>(null);
  const [selected, setSelected] = useState<string | null>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The catalogue comes from the server rather than being listed here, so a
  // new experience appears in this question on the deploy that adds it.
  useEffect(() => {
    let cancelled = false;
    businessExperienceService
      .get()
      .then(experience => {
        if (!cancelled) setOptions(experience.experiences);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only an answer the server OFFERS counts as chosen. Its list leaves out an
  // experience that comes with a module this business does not hold (field
  // service's), so a `current` naming one is kept by the api but not
  // preselected here: no radio would show it, so Continue waits for an answer
  // that is on screen.
  const chosen = options?.some(option => option.key === selected) ? selected : null;

  const save = useCallback(async () => {
    if (!chosen || saving) return;
    setSaving(true);
    setError(null);
    try {
      await businessService.updateBusiness(businessId, { default_experience: chosen });
      onDone(chosen);
    } catch (e) {
      // The api's own words, e.g. experience_requires_module's.
      setError(
        e instanceof BusinessApiError ? e.message : 'Could not save that. Please try again.'
      );
      setSaving(false);
    }
  }, [businessId, chosen, onDone, saving]);

  // An empty catalogue means the fetch failed. Say so and let them move on
  // rather than trapping them in front of a question with no answers — the
  // business already exists, and the guide will ask again.
  if (options !== null && options.length === 0) {
    return (
      <YStack gap="$3">
        <GBAlert
          variant="warning"
          message="We could not load the options just now. You can answer this later from your dashboard."
        />
        <XStack>
          <GBButton variant="secondary" onPress={() => onDone()}>
            Continue
          </GBButton>
        </XStack>
      </YStack>
    );
  }

  return (
    <YStack gap="$4">
      <YStack gap="$1">
        <GBText variant="titleMedium" bold>What does this business do?</GBText>
        <GBText variant="bodySmall" color="secondary">
          This sets which screens and figures Greenback puts in front of you. It changes
          nothing about what your books can hold, and you can change it whenever you like.
        </GBText>
      </YStack>

      {error && <GBAlert variant="error" message={error} />}

      {options === null ? (
        <YStack gap="$2">
          <GBSkeleton type="card" height={72} />
          <GBSkeleton type="card" height={72} />
        </YStack>
      ) : (
        <YStack gap="$2" role="radiogroup" aria-label="What does this business do?">
          {options.map(option => {
            const active = selected === option.key;
            return (
              <GBCard
                key={option.key}
                borderColor={active ? '$brand' : '$border'}
                borderWidth={active ? 2 : 1}
              >
                <GBCard.Body>
                  {/*
                    A REAL BUTTON, not a Tamagui stack with an onPress. Two
                    reasons, and the second is the one that bites: a native
                    control is focusable and activates on Enter and Space for
                    free, and `onPress` on an XStack is invisible to every test
                    in this repo — src/__mocks__/tamagui.js renders XStack as a
                    plain div and spreads onPress onto it, where it is not a
                    handler at all. Behaviour no test can reach is behaviour
                    that will break quietly.
                  */}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={option.name}
                    onClick={() => setSelected(option.key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      width: '100%',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <XStack alignItems="center" gap="$3">
                      <YStack flex={1} gap="$1">
                        <GBText variant="bodyLarge">{option.name}</GBText>
                        <GBText variant="bodySmall" color="secondary">{option.description}</GBText>
                      </YStack>
                      {active && <Check size={20} />}
                    </XStack>
                  </button>
                </GBCard.Body>
              </GBCard>
            );
          })}
        </YStack>
      )}

      <XStack gap="$3" alignItems="center" flexWrap="wrap">
        <GBButton variant="primary" disabled={!chosen || saving} onPress={save}>
          {saving ? 'Saving…' : 'Continue'}
        </GBButton>
        <GBButton variant="ghost" disabled={saving} onPress={() => onDone()}>
          {skipLabel}
        </GBButton>
      </XStack>
    </YStack>
  );
};

export default ExperienceQuestion;
